import type { TranslationAPI } from '@lumapps/translations';

import type { WidgetPlaylistProperties } from '../types';

export * from './playlistOptions/playlistOptions'; // switchPropertiesOptions

/**
 * Retrieves the first playlist ID for a given language with automatic fallback support.
 * Uses translateObject to handle language priority and fallback automatically.
 *
 * Handles multiple data formats for backward compatibility:
 * 1. Multi-language array: { playlist: { en: { ids: ['abc', 'def'] }, fr: { ids: ['xyz'] } } }
 * 2. Legacy single playlist: { playlist: { id: 'abc' } }
 *
 * @param playlist - Widget playlist containing playlist id
 * @param translateObject - Translation function to handle language fallback (from useTranslate hook)
 * @param isInDesignerMode - Whether in designer/editor mode (true) or viewer mode (false)
 * @param inputLanguage - Current input language (used in designer mode for direct language access)
 * @returns First playlist ID from the array for the selected language, or undefined if not found
 */
export const getPlaylistIdForLanguage = (
    playlist: WidgetPlaylistProperties['playlist'],
    translateObject: TranslationAPI['translateObject'],
    isInDesignerMode: boolean,
    inputLanguage: string,
): string | undefined => {
    if (!playlist) {
        return undefined;
    }

    const translatedId = isInDesignerMode
        ? playlist[inputLanguage]?.ids
        : (translateObject(playlist) as { ids: string[] })?.ids;

    // Check for multi-language: { playlist: { en: { ids: ['abc'] }, fr: { ids: ['def'] } } }
    if (translatedId && translatedId.length > 0) {
        return translatedId[0];
    }

    // Check for legacy single playlist structure: { playlist: { id: 'abc' } }
    if (!isInDesignerMode && 'id' in playlist && typeof playlist.id === 'string') {
        return playlist.id;
    }

    return undefined;
};
